import { HttpEvent, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, asyncScheduler, finalize, shareReplay, tap } from 'rxjs';

interface ICacheEntry {
  response$: Observable<HttpEvent<unknown>>;
  expiresAt: number;
}

@Injectable({ providedIn: 'root' })
export class ApiInterceptorCacheService {
  private cache = new Map<string, ICacheEntry>();

  public getOrCreate(
    key: string,
    factory: () => Observable<HttpEvent<unknown>>,
    ttlMs: number,
  ): Observable<HttpEvent<unknown>> {
    const now = Date.now();
    const cached = this.cache.get(key);

    if (cached && cached.expiresAt > now) {
      return cached.response$;
    }

    const response$ = factory().pipe(
      tap(() => {
        setTimeout(() => {
          this.cache.delete(key);
        }, ttlMs);
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
        scheduler: asyncScheduler,
        windowTime: ttlMs,
      }),
    );

    this.cache.set(key, {
      expiresAt: now + ttlMs,
      response$,
    });

    return response$;
  }

  public getOrCreateInFlight(
    key: string,
    factory: () => Observable<HttpEvent<unknown>>,
  ): Observable<HttpEvent<unknown>> {
    const cached = this.cache.get(key);

    if (cached) {
      return cached.response$;
    }

    const response$ = factory().pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
      finalize(() => {
        this.cache.delete(key);
      }),
    );

    this.cache.set(key, {
      expiresAt: 0,
      response$,
    });

    return response$;
  }

  public getCacheKey(request: HttpRequest<unknown>): string {
    const bodyHash = request.method !== 'GET' ? this.stableStringify(request.body ?? {}) : '';
    let output;

    switch (request.method) {
      case 'GET':
        output = `${request.method}|${request.urlWithParams}`;
        break;

      case 'POST':
        output = `${request.method}|${request.url}|${bodyHash}`;
        break;

      default:
        output = `${request.method}|${request.urlWithParams}|${bodyHash}`;
    }

    return output;
  }

  private stableStringify(value: unknown): string {
    if (value === null || typeof value !== 'object') {
      return JSON.stringify(value);
    }

    const sortedKeys = Object.keys(value as Record<string, unknown>).sort();

    return JSON.stringify(value, sortedKeys);
  }
}
